/**
 * Utility functions for browser locale detection and language mapping
 */

// Map browser locales to our supported ASR languages
const BROWSER_TO_ASR_LANGUAGE_MAP: Record<string, string> = {
  'en': 'en-US',
  'en-US': 'en-US',
  'en-GB': 'en-US',
  'en-CA': 'en-US',
  'en-AU': 'en-US',
  'fr': 'fr-FR',
  'fr-FR': 'fr-FR',
  'fr-CA': 'fr-FR',
  'fr-BE': 'fr-FR',
  'fr-CH': 'fr-FR',
  'de': 'de-DE',
  'de-DE': 'de-DE',
  'de-AT': 'de-DE',
  'de-CH': 'de-DE',
  'es': 'es-ES',
  'es-ES': 'es-ES',
  'es-MX': 'es-ES',
  'es-AR': 'es-ES',
  'it': 'it-IT',
  'it-IT': 'it-IT',
  'it-CH': 'it-IT',
  'pt': 'pt-PT',
  'pt-PT': 'pt-PT',
  'pt-BR': 'pt-PT',
  'nl': 'nl-NL',
  'nl-NL': 'nl-NL',
  'nl-BE': 'nl-NL',
  'sv': 'sv-SE',
  'sv-SE': 'sv-SE',
  'sv-FI': 'sv-SE',
  'pl': 'pl-PL',
  'pl-PL': 'pl-PL',
  'ja': 'ja-JP',
  'ja-JP': 'ja-JP',
  'he': 'he-IL',
  'he-IL': 'he-IL',
  'iw': 'he-IL', // legacy ISO 639-1 code for Hebrew still reported by some browsers
  'tr': 'tr-TR',
  'tr-TR': 'tr-TR'
}

// Map browser locales to UI languages (for i18next)
const BROWSER_TO_UI_LANGUAGE_MAP: Record<string, string> = {
  'en': 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'en-CA': 'en',
  'en-AU': 'en',
  'fr': 'fr',
  'fr-FR': 'fr',
  'fr-CA': 'fr',
  'fr-BE': 'fr',
  'fr-CH': 'fr',
  'de': 'de',
  'de-DE': 'de',
  'de-AT': 'de',
  'de-CH': 'de',
  'es': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  'es-AR': 'es',
  'it': 'it',
  'it-IT': 'it',
  'it-CH': 'it',
  'pt': 'pt',
  'pt-PT': 'pt',
  'pt-BR': 'pt',
  'nl': 'nl',
  'nl-NL': 'nl',
  'nl-BE': 'nl',
  'sv': 'sv',
  'sv-SE': 'sv',
  'sv-FI': 'sv',
  'pl': 'pl',
  'pl-PL': 'pl',
  'ja': 'ja',
  'ja-JP': 'ja',
  'he': 'he',
  'he-IL': 'he',
  'iw': 'he',
  'tr': 'tr',
  'tr-TR': 'tr'
}

// Available ASR languages
export const AVAILABLE_ASR_LANGUAGES = [
  'fr-FR',
  'fr-medicalV2-32',
  'en-US',
  'de-DE',
  'es-ES',
  'it-IT',
  'pt-PT',
  'nl-NL',
  'sv-SE',
  'pl-PL',
  'ja-JP',
  'he-IL',
  'tr-TR'
] as const

export type ASRLanguage = typeof AVAILABLE_ASR_LANGUAGES[number]

/**
 * Get the browser's default locale
 */
export function getBrowserLocale(): string {
  // Try navigator.language first, then navigator.languages array
  const browserLanguage = navigator.language || 
    (navigator.languages && navigator.languages[0]) || 
    'en-US'
  
  return browserLanguage
}

/**
 * Map browser locale to supported ASR language
 */
export function browserLocaleToASRLanguage(browserLocale: string): ASRLanguage {
  // Try exact match first
  if (BROWSER_TO_ASR_LANGUAGE_MAP[browserLocale]) {
    return BROWSER_TO_ASR_LANGUAGE_MAP[browserLocale] as ASRLanguage
  }
  
  // Try language code without region (e.g., 'fr' from 'fr-FR')
  const languageCode = browserLocale.split('-')[0]
  if (BROWSER_TO_ASR_LANGUAGE_MAP[languageCode]) {
    return BROWSER_TO_ASR_LANGUAGE_MAP[languageCode] as ASRLanguage
  }
  
  // Default to English if no match
  return 'en-US'
}

/**
 * Map browser locale to UI language (for i18next)
 */
export function browserLocaleToUILanguage(browserLocale: string): string {
  // Try exact match first
  if (BROWSER_TO_UI_LANGUAGE_MAP[browserLocale]) {
    return BROWSER_TO_UI_LANGUAGE_MAP[browserLocale]
  }
  
  // Try language code without region
  const languageCode = browserLocale.split('-')[0]
  if (BROWSER_TO_UI_LANGUAGE_MAP[languageCode]) {
    return BROWSER_TO_UI_LANGUAGE_MAP[languageCode]
  }
  
  // Default to English if no match
  return 'en'
}

/**
 * Map ASR language to UI language
 */
export function asrLanguageToUILanguage(asrLanguage: ASRLanguage): string {
  const asrToUIMap: Record<ASRLanguage, string> = {
    'fr-FR': 'fr',
    'fr-medicalV2-32': 'fr',
    'en-US': 'en',
    'de-DE': 'de',
    'es-ES': 'es',
    'it-IT': 'it',
    'pt-PT': 'pt',
    'nl-NL': 'nl',
    'sv-SE': 'sv',
    'pl-PL': 'pl',
    'ja-JP': 'ja',
    'he-IL': 'he',
    'tr-TR': 'tr'
  }

  return asrToUIMap[asrLanguage] || 'en'
}

/**
 * Get the default ASR language based on browser locale
 */
export function getDefaultASRLanguage(): ASRLanguage {
  const browserLocale = getBrowserLocale()
  return browserLocaleToASRLanguage(browserLocale)
}

/**
 * Get the default UI language based on browser locale
 */
export function getDefaultUILanguage(): string {
  const browserLocale = getBrowserLocale()
  return browserLocaleToUILanguage(browserLocale)
}

/**
 * Convert ASR language code to the format expected by the API (converts fr-FR to fr, etc.)
 */
export function convertLanguageCode(language: string): string {
  const languageMap: Record<string, string> = {
    'fr-FR': 'fr',
    'fr-medicalV2-32': 'fr-medicalV2-32',
    'en-US': 'en',
    'de-DE': 'de',
    'es-ES': 'es',
    'it-IT': 'it',
    'pt-PT': 'pt',
    'nl-NL': 'nl',
    'sv-SE': 'sv',
    'pl-PL': 'pl',
    'ja-JP': 'ja',
    'he-IL': 'he',
    'tr-TR': 'tr'
  }

  return languageMap[language] || 'en'
}